import { toPng } from 'html-to-image';

/**
 * PNG export of a chart, taken from the pixels the browser has already laid out rather than rebuilt
 * from the model. Two of the three renderings have no image to serialise on their own - the donut is
 * a CSS `conic-gradient` and the driver bars are div tracks - so the only way to ship the picture a
 * user is looking at, legend included, is to rasterise the node it is drawn in. `html-to-image`
 * clones that node into an SVG `foreignObject` and paints it to a canvas, which keeps the gradients,
 * the rounded fills and the inlined Inter faces the panel is drawn with.
 */
const FILENAME_FALLBACK = 'orbit-chart';

const DEVICE_PIXEL_RATIO = 2;

const pad = (value: number): string => (value < 10 ? `0${value}` : String(value));

const stamp = (now: Date): string =>
  `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}`;

export const slugifyMetricName = (metricName: string): string => {
  const slug = metricName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');

  return slug.length > 0 ? slug : FILENAME_FALLBACK;
};

export const orbitChartImageFilename = (metricName: string, now: Date = new Date()): string =>
  `${slugifyMetricName(metricName)}-${stamp(now)}.png`;

/**
 * `toPng` answers a base64 data URL; the download itself goes through an object URL so the href the
 * anchor carries stays short and is revoked the moment the click is dispatched.
 */
const pngBlobOf = (dataUrl: string): Blob => {
  const encoded = dataUrl.slice(dataUrl.indexOf(',') + 1);
  const binary = window.atob(encoded);
  const bytes = new Uint8Array(binary.length);

  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }

  return new Blob([bytes], { type: 'image/png' });
};

/**
 * Rasterises `node` and hands it to the browser. Resolves false where the object-URL API is missing
 * (jsdom, and any embedding that strips it) so the caller can tell "not downloaded" from
 * "downloaded"; a failure inside the rasteriser rejects and is the caller's to report.
 */
export const downloadOrbitChartImage = async (
  node: HTMLElement,
  metricName: string,
  backgroundColor: string
): Promise<boolean> => {
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return false;
  }

  const dataUrl = await toPng(node, { pixelRatio: DEVICE_PIXEL_RATIO, backgroundColor });
  const href = URL.createObjectURL(pngBlobOf(dataUrl));
  const anchor = document.createElement('a');

  anchor.href = href;
  anchor.download = orbitChartImageFilename(metricName);
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(href);

  return true;
};
